import { z } from 'zod';
import { ulid } from 'ulid';
import type {
	CreatePricingPolicyInput,
	UpdatePricingPolicyInput,
	RateTypeEnum
} from '@/graphql/generated';

const nonEmptyString = (field: string) =>
	z
		.string({ required_error: `${field} is required` })
		.trim()
		.min(1, `${field} is required`);

// Inline rate step schema
const rateStepSchema = z.object({
	rateStepId: z.string().optional(),
	intervalMinutes: z
		.number({ required_error: 'Interval is required', invalid_type_error: 'Interval must be a number' })
		.min(1, 'Interval must be at least 1 minute'),
	intervalCharge: z
		.number({ required_error: 'Charge is required', invalid_type_error: 'Charge must be a number' })
		.min(0, 'Charge must be 0 or greater'),
	maxIntervals: z.number().min(1, 'Max intervals must be at least 1').nullish()
});

// Inline rate schema
const inlineRateSchema = z.object({
	rateId: z.string().optional(),
	rateTypeEnum: z.enum(['TIERED', 'FLAT_RATE'], { required_error: 'Rate type is required' }),
	flatRateAmount: z.number().min(0, 'Flat rate must be 0 or greater').nullish(),
	rateSteps: z.array(rateStepSchema).optional()
}).superRefine((data, ctx) => {
	if (data.rateTypeEnum === 'TIERED') {
		if (!data.rateSteps || data.rateSteps.length === 0) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'At least one pricing step is required for tiered rates',
				path: ['rateSteps']
			});
		}
	}
	if (data.rateTypeEnum === 'FLAT_RATE') {
		if (data.flatRateAmount == null || data.flatRateAmount <= 0) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Flat rate amount must be greater than 0',
				path: ['flatRateAmount']
			});
		}
	}
});

// Inline limit schema (all fields optional)
const inlineLimitSchema = z.object({
	limitId: z.string().optional(),
	minAmount: z.number().min(0).nullish(),
	maxAmount: z.number().min(0).nullish(),
	minStayMinutes: z.number().int().min(0).nullish(),
	maxStayMinutes: z.number().int().min(0).nullish()
}).optional();

export const pricingPolicyFormSchema = z.object({
	name: nonEmptyString('Name'),
	priority: z
		.string({ required_error: 'Priority is required' })
		.trim()
		.regex(/^[0-9]+$/, 'Priority must be a whole number'),
	isActive: z.boolean(),
	tariffId: nonEmptyString('Tariff'),
	scenarioId: z.string().trim().optional(),
	rate: inlineRateSchema,
	overstayRate: inlineRateSchema.optional(),
	limit: inlineLimitSchema,
	billingCycleId: nonEmptyString('Billing cycle')
});

export type PricingPolicyFormValues = z.infer<typeof pricingPolicyFormSchema>;

const parseRequiredInteger = (value: string, fieldName: string): number => {
	const parsed = Number.parseInt(value, 10);

	if (Number.isNaN(parsed)) {
		throw new Error(`${fieldName} is invalid.`);
	}

	return parsed;
};

const sanitiseName = (value: string) => value.trim();

const buildRateSteps = (
	steps: Array<{ rateStepId?: string; intervalMinutes: number; intervalCharge: number; maxIntervals?: number | null }> | undefined
) =>
	(steps ?? []).map((step, index) => ({
		rateStepId: step.rateStepId || ulid(),
		stepSequence: index + 1,
		intervalMinutes: step.intervalMinutes,
		intervalCharge: step.intervalCharge ?? 0,
		maxIntervals: step.maxIntervals ?? undefined
	}));

const buildRateInput = (rate: PricingPolicyFormValues['rate']) => ({
	rateId: rate.rateId || ulid(),
	rateTypeEnum: rate.rateTypeEnum as RateTypeEnum,
	flatRateAmount: rate.rateTypeEnum === 'FLAT_RATE' ? (rate.flatRateAmount ?? undefined) : undefined,
	rateSteps: buildRateSteps(rate.rateTypeEnum === 'TIERED' ? rate.rateSteps : [])
});

const buildOptionalRateInput = (rate: PricingPolicyFormValues['overstayRate']) => {
	if (!rate || !rate.rateTypeEnum) return undefined;
	return buildRateInput(rate);
};

const buildLimitInput = (limit: PricingPolicyFormValues['limit']) => {
	if (!limit) return undefined;
	const hasAnyValue = limit.minAmount != null || limit.maxAmount != null ||
		limit.minStayMinutes != null || limit.maxStayMinutes != null;
	if (!hasAnyValue) return undefined;
	return {
		limitId: limit.limitId || ulid(),
		minAmount: limit.minAmount ?? undefined,
		maxAmount: limit.maxAmount ?? undefined,
		minStayMinutes: limit.minStayMinutes ?? undefined,
		maxStayMinutes: limit.maxStayMinutes ?? undefined
	};
};

export const buildCreatePricingPolicyInput = (
	values: PricingPolicyFormValues,
	pricingPolicyId: string
): CreatePricingPolicyInput => ({
	pricingPolicyId,
	name: sanitiseName(values.name),
	priority: parseRequiredInteger(values.priority, 'Priority'),
	isActive: values.isActive,
	tariffId: values.tariffId,
	scenarioId: values.scenarioId || undefined,
	rate: buildRateInput(values.rate),
	overstayRate: buildOptionalRateInput(values.overstayRate),
	limit: buildLimitInput(values.limit),
	billingCycleId: parseRequiredInteger(values.billingCycleId, 'Billing cycle')
});

export const buildUpdatePricingPolicyInput = (
	values: PricingPolicyFormValues,
	pricingPolicyId: string
): UpdatePricingPolicyInput => ({
	pricingPolicyId,
	name: sanitiseName(values.name),
	priority: parseRequiredInteger(values.priority, 'Priority'),
	isActive: values.isActive,
	tariffId: values.tariffId,
	scenarioId: values.scenarioId || undefined,
	rate: buildRateInput(values.rate),
	overstayRate: buildOptionalRateInput(values.overstayRate),
	limit: buildLimitInput(values.limit),
	billingCycleId: parseRequiredInteger(values.billingCycleId, 'Billing cycle')
});
